import { IconButton } from '@mui/material'
import type { FC } from 'react'
import { Suspense, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import IcBaselineClose from '~icons/ic/baseline-close'
import LoadingPage from 'src/components/LoadingPage'
import type { SubmitFormProps } from 'src/components/SubmitArtwork/SubmitForm'
import { SubmitForm } from 'src/components/SubmitArtwork/SubmitForm'
import { UIDialog, UIDialogHeader } from 'src/components/ui/Modal'
import type { ArtworkDetailFragment } from 'src/services/apollo/generated/graphql'

interface EditArtworkDialogContentProps extends SubmitFormProps {
  close: () => void
  artwork: ArtworkDetailFragment
}

const EditArtworkDialogContent: FC<EditArtworkDialogContentProps> = ({
  close,
  artwork,
  ...submitFormProps
}) => {
  const { t } = useTranslation(['artwork'])

  const initialTags = useMemo(() => {
    if (artwork.tacks && artwork.tacks.length > 0) {
      return artwork.tacks.map(tack => ({
        id: tack.id,
        name: tack.codeName,
        displayName: tack.displayName ?? tack.defaultName,
        mediaId: tack.mediaId,
      }))
    }
    return []
  }, [artwork.tacks])

  return (
    <Suspense fallback={<LoadingPage />}>
      <UIDialogHeader
        heading={
          <span className="inline-flex w-full items-center justify-between">
            {t('artwork:edit-artwork-form-title')}
            <IconButton edge="end" onClick={close}>
              <IcBaselineClose />
            </IconButton>
          </span>
        }
      ></UIDialogHeader>
      <SubmitForm
        key={`${artwork.id}`}
        onSuccess={close}
        artworkId={artwork.id}
        media={artwork.media}
        initialValues={{
          ...artwork,
          description: artwork.extra?.description || '',
          tags: initialTags,
        }}
        fields={{
          description: true,
        }}
        allowEditImage={false}
        {...submitFormProps}
      />
    </Suspense>
  )
}

export const dialogEditArtwork = async (
  artwork: ArtworkDetailFragment,
  props?: SubmitFormProps,
) => {
  return UIDialog.create({
    dismissable: true,
    children: ({ close }) => (
      <EditArtworkDialogContent artwork={artwork} close={close} {...props} />
    ),
    className: 'max-w-screen-mlg',
  })
}
